import { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { buildWhatsAppUrl } from "../../content/siteContent";

const reveal = {
  hidden: { opacity: 0, y: 34, scale: 0.985, filter: "blur(10px)" },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: { duration: 0.68, ease: [0.22, 1, 0.36, 1] },
  },
};

export const Reveal = ({ children, className = "", delay = 0, hover = false }) => (
  <motion.div
    className={className}
    variants={reveal}
    initial="hidden"
    whileInView="visible"
    whileHover={hover ? { y: -10, scale: 1.01 } : undefined}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ delay }}
  >
    {children}
  </motion.div>
);

const FadeSlider = ({ images, title, testId }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % images.length);
    }, 3800);

    return () => window.clearInterval(interval);
  }, [images]);

  return (
    <div className="image-frame hero-slider-frame min-h-[320px] lg:min-h-full" data-testid={`${testId}-image-frame`}>
      <AnimatePresence mode="wait">
        <motion.img
          key={images[activeIndex]}
          src={images[activeIndex]}
          alt={title}
          className="hero-slider-image absolute inset-0 h-full w-full object-cover object-center"
          loading={activeIndex === 0 ? "eager" : "lazy"}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.985 }}
          transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
          data-testid={`${testId}-image-${activeIndex + 1}`}
        />
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-t from-brand via-brand/10 to-transparent" />
      <div className="absolute bottom-5 right-5 z-10 flex gap-2" data-testid={`${testId}-slider-dots`}>
        {images.map((image, index) => (
          <button
            key={image}
            type="button"
            onClick={() => setActiveIndex(index)}
            className={`h-2.5 transition-all duration-300 ${
              activeIndex === index ? "w-10 bg-safety" : "w-2.5 bg-white/45 hover:bg-white/70"
            }`}
            aria-label={`Ir para slide ${index + 1}`}
            data-testid={`${testId}-slider-dot-${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export const SectionHeader = ({ eyebrow, title, description, light = false, testId }) => (
  <Reveal className="max-w-3xl space-y-5">
    <span className={`eyebrow ${light ? "bg-white text-brand" : ""}`} data-testid={`${testId}-eyebrow`}>
      {eyebrow}
    </span>
    <h2 className={light ? "display-title" : "section-title"} data-testid={`${testId}-title`}>
      {title}
    </h2>
    <p className={`body-copy ${light ? "text-white/80" : ""}`} data-testid={`${testId}-description`}>
      {description}
    </p>
  </Reveal>
);

export const PageHero = ({ eyebrow, title, description, image, images = [], ctaMessage, testId }) => {
  const sliderImages = images.length ? images : [image];

  return (
    <section className="container-shell pb-12" data-testid={`${testId}-section`}>
      <div className="grid overflow-hidden steel-panel lg:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-7 px-6 py-12 md:px-10 md:py-14 lg:px-14 lg:py-16">
          <span className="eyebrow" data-testid={`${testId}-eyebrow`}>
            {eyebrow}
          </span>
          <Reveal>
            <h1 className="display-title max-w-3xl" data-testid={`${testId}-title`}>
              {title}
            </h1>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="max-w-2xl body-copy text-white/80" data-testid={`${testId}-description`}>
              {description}
            </p>
          </Reveal>
          <Reveal delay={0.14} className="flex flex-col gap-4 sm:flex-row">
            <a
              href={buildWhatsAppUrl(ctaMessage)}
              target="_blank"
              rel="noreferrer"
              className="primary-button group"
              data-testid={`${testId}-cta-button`}
            >
              Solicitar orçamento
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <Link
              to="/contato"
              className="inline-flex items-center justify-center gap-2 border border-white/60 bg-gradient-to-r from-white/10 via-white/5 to-white/15 px-6 py-4 font-heading text-base font-bold uppercase tracking-[0.18em] text-white shadow-[0_18px_40px_rgba(0,0,0,0.12)] transition duration-300 hover:-translate-y-1.5 hover:border-safety hover:bg-white/10"
              data-testid={`${testId}-secondary-link`}
            >
              Falar com especialista
            </Link>
          </Reveal>
        </div>

        <FadeSlider images={sliderImages} title={title} testId={testId} />
      </div>
    </section>
  );
};

export const ServiceCard = ({ service, index = 0 }) => (
  <Reveal delay={index * 0.05} className="group h-full" hover>
    <div className="surface-card service-card-shell grid h-full md:grid-cols-[0.9fr_1.1fr]" data-testid={`service-card-${index + 1}`}>
      <div className="relative min-h-[240px] bg-brand/10">
        <img
          src={service.image}
          alt={service.title}
          className="absolute inset-0 h-full w-full object-cover object-center transition duration-700 group-hover:scale-105"
          loading="lazy"
          data-testid={`service-card-image-${index + 1}`}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand/40 via-transparent to-safety/10" />
      </div>
      <div className="space-y-5 p-6 md:p-8">
        <span className="text-xs font-semibold uppercase tracking-[0.3em] text-safety" data-testid={`service-card-eyebrow-${index + 1}`}>
          {service.eyebrow}
        </span>
        <h3 className="font-heading text-3xl uppercase text-brand" data-testid={`service-card-title-${index + 1}`}>
          {service.title}
        </h3>
        <p className="body-copy" data-testid={`service-card-description-${index + 1}`}>
          {service.summary}
        </p>
        <Link
          to={service.path}
          className="secondary-button group"
          data-testid={`service-card-link-${index + 1}`}
        >
          Saiba mais
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  </Reveal>
);

export const InfoCard = ({ title, description, items = [], testId, dark = false }) => (
  <Reveal className={`${dark ? "dark-card" : "surface-card"} h-full p-6 md:p-8`} hover>
    <div className="space-y-5" data-testid={testId}>
      <h3 className={`font-heading text-3xl uppercase ${dark ? "text-white" : "text-brand"}`} data-testid={`${testId}-title`}>
        {title}
      </h3>
      <p className={`body-copy ${dark ? "text-white/80" : ""}`} data-testid={`${testId}-description`}>
        {description}
      </p>
      {items.length ? (
        <ul className={`safe-list ${dark ? "text-white/85" : ""}`} data-testid={`${testId}-list`}>
          {items.map((item) => (
            <li key={item} data-testid={`${testId}-item-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {item}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  </Reveal>
);

export const MetricCard = ({ value, label, testId }) => (
  <Reveal className="dark-card p-6 text-center" hover>
    <div className="space-y-3" data-testid={testId}>
      <div className="font-heading text-5xl uppercase text-safety" data-testid={`${testId}-value`}>
        {value}
      </div>
      <p className="text-sm uppercase tracking-[0.18em] text-white/70" data-testid={`${testId}-label`}>
        {label}
      </p>
    </div>
  </Reveal>
);

export const TestimonialCard = ({ item, index }) => (
  <Reveal delay={index * 0.07} className="surface-card h-full p-6 md:p-8" hover>
    <div className="space-y-5" data-testid={`testimonial-card-${index + 1}`}>
      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-safety" data-testid={`testimonial-company-${index + 1}`}>
        {item.company}
      </span>
      <p className="text-lg leading-8 text-slate-700" data-testid={`testimonial-quote-${index + 1}`}>
        “{item.quote}”
      </p>
      <p className="text-sm uppercase tracking-[0.16em] text-brand" data-testid={`testimonial-author-${index + 1}`}>
        {item.author}
      </p>
    </div>
  </Reveal>
);

export const HighlightList = ({ items, testId, light = false }) => (
  <div className="grid gap-4" data-testid={testId}>
    {items.map((item) => (
      <Reveal key={item}>
        <div
          className={`flex items-start gap-3 border-l-4 border-safety px-4 py-3 shadow-[0_18px_40px_rgba(15,23,42,0.08)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_26px_60px_rgba(11,31,58,0.16)] ${
            light ? "bg-white/10 text-white" : "bg-gradient-to-br from-white via-white to-slate-100 text-slate-700"
          }`}
          data-testid={`${testId}-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-safety" />
          <span>{item}</span>
        </div>
      </Reveal>
    ))}
  </div>
);

export const CTASection = ({ title, description, buttonLabel, buttonMessage, testId }) => (
  <section className="container-shell py-12" data-testid={`${testId}-section`}>
    <Reveal className="steel-panel grid gap-8 px-6 py-10 md:px-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
      <div className="space-y-4">
        <h2 className="display-title text-4xl md:text-5xl" data-testid={`${testId}-title`}>
          {title}
        </h2>
        <p className="body-copy text-white/80" data-testid={`${testId}-description`}>
          {description}
        </p>
      </div>
      <div className="flex flex-col gap-4 sm:flex-row lg:justify-end">
        <a
          href={buildWhatsAppUrl(buttonMessage)}
          target="_blank"
          rel="noreferrer"
          className="primary-button group justify-center"
          data-testid={`${testId}-button`}
        >
          {buttonLabel}
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </div>
    </Reveal>
  </section>
);